import { HttpStatusCode } from "axios";
import { useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  ApiException,
  ApiSuffixType,
  CrudApi,
  errors,
  getDynamicEditFormRootAndMap,
  IDynamicEditFormRoot,
  Log,
  RydeUiUnit,
  schemaRootAF,
  warnAndToastException,
} from "@ryde/shared";

import { useSimpleNavigation } from "../../../navigation";

interface IUseSchemaResult {
  fetchSchema: () => Promise<void>;
}

export const useSchema = (entity: RydeUiUnit): IUseSchemaResult => {
  const { t } = useTranslation();
  const { navigatePageNotFound } = useSimpleNavigation();
  const [rootSchema, setRootSchema] = useRecoilState(schemaRootAF(entity));
  const [isPending, startTransition] = useTransition();

  const fetchAndFillSchema = async (): Promise<void> => {
    try {
      const schemeData = await CrudApi.getOne<IDynamicEditFormRoot>(
        entity,

        ApiSuffixType.DynamicFormsScheme,
      );

      if (!schemeData) {
        return;
      }

      Log.debug(`Screen schema for ${entity} set to: `, schemeData);
      setRootSchema(getDynamicEditFormRootAndMap(schemeData));
    } catch (error) {
      if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
        warnAndToastException({
          error,
          fallbackTranslationKey: errors.common.dontHavePermissionForAction,
          translationFunction: t,
        });
        return;
      }
      Log.errorAndToast(
        t(errors.common.invalidAPIResponse, "Error fetching page schema"),
      );
    }
  };

  const fetchSchema = useEvent(async (): Promise<void> => {
    if (rootSchema || isPending) {
      Log.debug(`Trying fetch already exists Schema for entity: ${entity}`);
      return;
    }

    if (entity === "unknown") {
      Log.errorAndToast(
        t("errors.tryFetchingSchemaForUnknownEntity", { entity: entity }),
      );
      return;
    }

    startTransition(() => {
      fetchAndFillSchema();
    });
  });

  return { fetchSchema };
};
